import { fieldConfig, ZodProvider } from "@autoform/zod";
import { CircularProgressIndicator } from "@components/shared/CircularProgressIndicator";
import { addressSchema } from "@components/ui/address-autocomplete";
import { AutoForm } from "@components/ui/autoform";
import { SubmitButton } from "@components/ui/autoform/components/SubmitButton";
import { useLocations } from "@hooks/useLocations";
import { MdAddLocation } from "react-icons/md";
import { Location } from "types";
import { v4 as uuid } from "uuid"
import { z } from "zod";

export interface AddLocationFormData {
    name: string;
    formatted_address: string;
    country: string;
    address1: string;
    address2?: string;
    lat?: number;
    lng?: number;
    city: string;
    state: string;
    zipcode: string;
    phone: string;
}

function AddLocationDialog({
    location,
    onSuccess,
}: {
    location?: Location;
    onSuccess: (locationId: string) => void;
}) {
    const { createLocation, isCreatingLocation, updateLocation, isUpdatingLocation } = useLocations();

    const isPending = isCreatingLocation || isUpdatingLocation;

    function onSubmit(formData: AddLocationFormData) {
        const data = { id: location?.id ?? uuid(), ...formData }
        if (location?.id) {
            updateLocation(data);
        } else {
            createLocation(data);
        }
        onSuccess(data.id)
    }

    return (
        <AutoForm
            defaultValues={{
                phone: location?.phone ?? "",
                name: location?.name ?? "",
                address: {
                    address1: location?.address1 ?? "",
                    address2: location?.address2 ?? "",
                    lat: location?.lat ?? 0,
                    lng: location?.lng ?? 0,
                    city: location?.city ?? "",
                    postalCode: location?.zipcode ?? "",
                    region: location?.state ?? "",
                    country: location?.country ?? "",
                    formattedAddress: location?.formatted_address ?? "",
                },
            }}
            onSubmit={(data) => {
                onSubmit({
                    phone: data.phone ?? "",
                    address1: data.address.address1,
                    city: data.address.city,
                    zipcode: data.address.postalCode ?? "NA",
                    state: data.address.region,
                    country: data.address.country,
                    name: data.name ?? data.address.formattedAddress ?? "",
                    formatted_address: data.address.formattedAddress ?? data.name ?? "",
                    address2: data.address.address2,
                    lat: data.address.lat,
                    lng: data.address.lng,
                });
            }}
            schema={
                new ZodProvider(
                    z.object({
                        name: z
                            .string()
                            .min(1, "Please enter a name"),
                        address: addressSchema.superRefine(
                            fieldConfig({
                                fieldType: "address",
                            }),
                        ),
                        phone: z
                            .string()
                            .min(12, "Please enter a valid phone number")
                            .max(14, "Please enter a valid phone number")
                            .optional()
                            .superRefine(
                                fieldConfig({
                                    fieldType: "phone",
                                }),
                            ),
                    }),
                )
            }
        >
            <SubmitButton>
                {isPending ? (
                    <CircularProgressIndicator />
                ) : (
                    <MdAddLocation />
                )}
                {location ? "Update Location" : "Add Location"}
            </SubmitButton>
        </AutoForm>
    );
}

export default AddLocationDialog;
